import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';

export interface IndianState {
  name: string;
  gstCode: string;
}

export interface Country {
  name: string;
  code: string;
}

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss']
})
export class OnboardingComponent implements OnInit {
  form!: FormGroup;
  currentStep = 1;
  totalSteps = 3;
  isLoading = false;
  isPasswordVisible = false;
  token: string | null = null;

  // API response state
  submitSuccess = false;
  submitError = false;
  submitMessage = '';

  // Token verification state
  tokenVerifying = false;
  tokenInvalid = false;

  // Countries — India only for now; add more when scaling internationally
  readonly countries: Country[] = [
    { name: 'India', code: 'IN' },
    // Future: { name: 'United States', code: 'US' },
    // Future: { name: 'United Arab Emirates', code: 'AE' },
  ];

  readonly indianStates: IndianState[] = [
    { name: 'Andhra Pradesh', gstCode: '37' },
    { name: 'Arunachal Pradesh', gstCode: '12' },
    { name: 'Assam', gstCode: '18' },
    { name: 'Bihar', gstCode: '10' },
    { name: 'Chhattisgarh', gstCode: '22' },
    { name: 'Goa', gstCode: '30' },
    { name: 'Gujarat', gstCode: '24' },
    { name: 'Haryana', gstCode: '06' },
    { name: 'Himachal Pradesh', gstCode: '02' },
    { name: 'Jharkhand', gstCode: '20' },
    { name: 'Karnataka', gstCode: '29' },
    { name: 'Kerala', gstCode: '32' },
    { name: 'Madhya Pradesh', gstCode: '23' },
    { name: 'Maharashtra', gstCode: '27' },
    { name: 'Manipur', gstCode: '14' },
    { name: 'Meghalaya', gstCode: '17' },
    { name: 'Mizoram', gstCode: '15' },
    { name: 'Nagaland', gstCode: '13' },
    { name: 'Odisha', gstCode: '21' },
    { name: 'Punjab', gstCode: '03' },
    { name: 'Rajasthan', gstCode: '08' },
    { name: 'Sikkim', gstCode: '11' },
    { name: 'Tamil Nadu', gstCode: '33' },
    { name: 'Telangana', gstCode: '36' },
    { name: 'Tripura', gstCode: '16' },
    { name: 'Uttar Pradesh', gstCode: '09' },
    { name: 'Uttarakhand', gstCode: '05' },
    { name: 'West Bengal', gstCode: '19' },
    { name: 'Andaman and Nicobar Islands', gstCode: '35' },
    { name: 'Chandigarh', gstCode: '04' },
    { name: 'Dadra and Nagar Haveli and Daman and Diu', gstCode: '26' },
    { name: 'Delhi', gstCode: '07' },
    { name: 'Jammu and Kashmir', gstCode: '01' },
    { name: 'Ladakh', gstCode: '38' },
    { name: 'Lakshadweep', gstCode: '31' },
    { name: 'Puducherry', gstCode: '34' },
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      // Step 1: Business Details
      client_name: ['', [Validators.required, Validators.minLength(2)]],
      client_email: ['', [Validators.required, Validators.email]],
      client_contact: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],

      // Step 2: User Details
      first_name: ['', [Validators.required, Validators.minLength(2)]],
      last_name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],

      // Step 3: Billing
      billing_name: ['', [Validators.required, Validators.minLength(2)]],
      billing_email: ['', [Validators.required, Validators.email]],
      billing_phone: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      billing_address_line1: ['', [Validators.required, Validators.minLength(5)]],
      billing_city: ['', Validators.required],
      billing_state: ['', Validators.required],
      billing_pincode: ['', [Validators.required, Validators.pattern('^[0-9]{6}$')]],
      // Scalable country dropdown — defaults to India
      billing_country: ['India', Validators.required],
      // Hidden internal field — auto-populated from state selection, never shown to user
      gst_state_code: ['']
    });

    // Auto-fill GST code on state change
    this.form.get('billing_state')?.valueChanges.subscribe(stateName => {
      const found = this.indianStates.find(s => s.name === stateName);
      if (found) {
        this.form.get('gst_state_code')?.setValue(found.gstCode);
      } else {
        this.form.get('gst_state_code')?.setValue('');
      }
    });

    this.route.params.subscribe(params => {
      this.token = params['token'];
      if (this.token) {
        this.verifyOnboardingToken(this.token);
      } else {
        this.tokenInvalid = true;
      }
    });
  }

  verifyOnboardingToken(token: string) {
    this.tokenVerifying = true;
    this.http.post(environment.verifyOnboarding, { token }).subscribe({
      next: (res: any) => {
        this.tokenVerifying = false;
        if (res && res.success === false) {
          this.tokenInvalid = true;
          this.submitError = true;
          this.submitMessage = res.message || 'Invalid or expired onboarding link.';
          return;
        }
        if (res && res.data) {
          this.form.patchValue({
            client_name: res.data.client_name || res.data.name || '',
            client_email: res.data.client_email || res.data.email || '',
            client_contact: res.data.client_contact || res.data.contact || ''
          });
        }
      },
      error: (err) => {
        this.tokenVerifying = false;
        this.tokenInvalid = true;
        this.submitError = true;
        this.submitMessage = err?.error?.message || 'Invalid or expired onboarding link. Please contact support.';
      }
    });
  }

  get stepOneFields() { return ['client_name', 'client_email', 'client_contact']; }
  get stepTwoFields() { return ['first_name', 'last_name', 'email', 'password']; }
  get stepThreeFields() { return ['billing_name', 'billing_email', 'billing_phone', 'billing_address_line1', 'billing_city', 'billing_state', 'billing_pincode']; }

  nextStep() {
    let fieldsToValidate: string[] = [];

    if (this.currentStep === 1) {
      fieldsToValidate = this.stepOneFields;
    } else if (this.currentStep === 2) {
      fieldsToValidate = this.stepTwoFields;
    }

    const isInvalid = fieldsToValidate.some(field => this.form.get(field)?.invalid);
    if (isInvalid) {
      fieldsToValidate.forEach(field => this.form.get(field)?.markAsTouched());
      return;
    }
    this.currentStep++;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  getFieldError(fieldName: string): string {
    const control = this.form.get(fieldName);
    if (control?.touched && control?.errors) {
      if (control.errors['required']) return 'This field is required.';
      if (control.errors['email']) return 'Please enter a valid email address.';
      if (control.errors['pattern']) {
        if (fieldName.includes('phone') || fieldName.includes('contact')) {
          return 'Please enter a valid 10-digit phone number.';
        }
        if (fieldName.includes('pincode')) {
          return 'Please enter a valid 6-digit pincode.';
        }
        return 'Invalid format.';
      }
      if (control.errors['minlength']) {
        const min = control.errors['minlength'].requiredLength;
        return `Minimum ${min} characters required.`;
      }
    }
    return '';
  }

  hasError(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    return !!(control?.touched && control?.invalid);
  }

  onSubmit() {
    const fieldsToValidate = this.stepThreeFields;
    const isInvalid = fieldsToValidate.some(field => this.form.get(field)?.invalid);
    if (isInvalid) {
      fieldsToValidate.forEach(field => this.form.get(field)?.markAsTouched());
      return;
    }

    this.isLoading = true;
    this.submitSuccess = false;
    this.submitError = false;
    this.submitMessage = '';

    const val = this.form.value; // all fields are enabled — billing_country & gst_state_code readable directly
    const payload = {
      token: this.token,
      body: {
        billing_name: val.billing_name,
        billing_email: val.billing_email,
        billing_phone: val.billing_phone,
        billing_address_line1: val.billing_address_line1,
        billing_city: val.billing_city,
        billing_state: val.billing_state,
        billing_pincode: val.billing_pincode,
        billing_country: val.billing_country,   // from dropdown — scalable
        gst_state_code: val.gst_state_code,     // hidden internal field, auto-derived from state
        first_name: val.first_name,
        last_name: val.last_name,
        password: val.password,
        email: val.email
      }
    };

    this.http.post(environment.completeOnboarding, payload).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res && res.success === false) {
          this.submitError = true;
          this.submitMessage = res.message || 'Setup failed. Please try again.';
        } else {
          this.submitSuccess = true;
          this.submitMessage = res?.message || 'Your account has been set up successfully. Redirecting to login...';
          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 3500);
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.submitError = true;
        this.submitMessage = err?.error?.message || 'Something went wrong. Please try again or contact support.';
      }
    });
  }

  get progressPercent(): number {
    return Math.round(((this.currentStep - 1) / (this.totalSteps - 1)) * 100);
  }

  get f() { return this.form.controls; }
}
